export interface Position {
  x: number;
  y: number;
}

export interface Size {
  width: number;
  height: number;
}

export interface TextStyle {
  fontSize: number;
  fontFamily: string;
  fontWeight: string;
  color: string;
  textAlign: 'left' | 'center' | 'right' | 'justify';
  italic: boolean;
  underline: boolean;
}

export interface CornerRadii {
  tl?: number;
  tr?: number;
  br?: number;
  bl?: number;
}

export interface ElementShadow {
  offsetX: number;
  offsetY: number;
  blur: number;
  color: string;
}

export function elementShadowToCss(s?: ElementShadow, scale: number = 1): string | undefined {
  if (!s) return undefined;
  return `${s.offsetX * scale}px ${s.offsetY * scale}px ${s.blur * scale}px ${s.color}`;
}

export function cornerRadiiToCss(cr?: CornerRadii, scale: number = 1): string | undefined {
  if (!cr) return undefined;
  const { tl = 0, tr = 0, br = 0, bl = 0 } = cr;
  if (!tl && !tr && !br && !bl) return undefined;
  return `${tl * scale}px ${tr * scale}px ${br * scale}px ${bl * scale}px`;
}

export interface YearbookElement {
  id: string;
  type: 'text' | 'image' | 'slot' | 'free_slot' | 'shape' | 'shape_border';
  position: Position;
  size: Size;
  rotation: number;
  content?: string;
  textStyle?: TextStyle;
  imageUrl?: string;
  imageRatio?: number;
  imageOffset?: Position;
  imageScale?: number; // zoom multiplier for slot images (1 = fit-cover, >1 = zoomed in)
  fromLayout?: boolean;
  /** true if the user added it manually; false/undefined if it comes from a template. Used to preserve user elements when switching layouts. */
  userAdded?: boolean;
  pictureId?: number;
  /** Identifiant de l'élève associé (trombinoscope) — sert à faire matcher les mises à jour. */
  userId?: number;
  locked?: boolean;
  isSticker?: boolean;
  shapeType?: 'rect' | 'line' | 'circle';
  zPriority?: 'front' | 'back';
  thumbnail?: string;
  previewImageUrl?: string;
  isCircle?: boolean;
  backgroundColor?: string;
  qrcodeId?: number;
  borderRadius?: number;
  strokeWidth?: number;
  preCircleSize?: Size;
  preCirclePosition?: Position;
  forceDimension?: boolean;
  cornerRadii?: CornerRadii;
  borderColor?: string;
  shadow?: ElementShadow;
  zIndex?: number;
  /** Elements sharing the same groupId are selected/deleted together. */
  groupId?: string;
  /** Sticker rendering format. 'svg' enables inline recolorable SVG. 'simple_svg' renders inline SVG but keeps original colors (no color picker). */
  stickerFormat?: 'png' | 'svg' | 'simple_svg';
  /** Current color applied to an SVG sticker (used as `currentColor`). */
  stickerColor?: string;
  /** Mots interdits détectés côté back sur ce texte (lecture seule, non renvoyé au save). */
  forbiddenWords?: string[];
}

export interface PageBackground {
  type: 'color' | 'image';
  value: string;
  trame_background_id?: number | null;
  trame_background_custom_id?: number | null;
}

export interface PageGuide {
  id: string;
  orientation: 'horizontal' | 'vertical';
  /** Absolute position in pt from the top edge (horizontal) or left edge (vertical). */
  position: number;
}

export interface YearbookPage {
  id: string;
  pageNumber: number;
  elements: YearbookElement[];
  background: PageBackground;
  layoutTemplateId?: number | null;
  coverType?: 'front' | 'back' | 'tranche' | null;
  name?: string | null;
  noteToGraphiste?: string;
  itemType?: string;
  /** Optional override of the page width (in pt). Used by the spine ("tranche") which is narrower than a regular page. */
  pageWidth?: number;
  /** User-defined alignment guides (rulers). Editor-only, hidden in preview/overview. */
  guides?: PageGuide[];
}

export type ToolType = 'inspiration' | 'trombi' | 'photos' | 'layout' | 'cover' | 'background' | 'text' | 'stickers' | 'qrcode' | 'crossword';

export type ViewMode = 'page' | 'overview' | 'preview';

export const DEFAULT_TEXT_STYLE: TextStyle = {
  fontSize: 18,
  fontFamily: 'DM Sans',
  fontWeight: '400',
  color: '#1a1a2e',
  textAlign: 'left',
  italic: false,
  underline: false,
};

export const DEFAULT_PAGE_WIDTH = 595;
export const DEFAULT_PAGE_HEIGHT = 842;

export let PAGE_WIDTH = DEFAULT_PAGE_WIDTH;
export let PAGE_HEIGHT = DEFAULT_PAGE_HEIGHT;

export function setPageDimensions(width: number, height: number) {
  PAGE_WIDTH = width;
  PAGE_HEIGHT = height;
}

export const BACKGROUND_COLORS = [
  '#ffffff', '#f8f9fa', '#e9ecef', '#dee2e6',
  '#fff3cd', '#ffeaa7', '#fdcb6e', '#f39c12',
  '#d4edda', '#a8e6cf', '#55efc4', '#00b894',
  '#cce5ff', '#74b9ff', '#0984e3', '#2d3436',
  '#f8d7da', '#fab1a0', '#e17055', '#d63031',
  '#e2d5f1', '#a29bfe', '#6c5ce7', '#34495e',
  '#1a1a2e', '#16213e', '#0f3460', '#533483',
];
